import { TemplateStudioModel } from 'common/contracts';
import {
  DEFAULT_BODY_BLOCKS_ORDER,
  EFeatureFlag,
  StoreBodyBlock
} from 'constants/enums';
import { TemplateStudioBlock } from 'state/template-studio.state.service';

import {
  bundleDesignsById,
  bundleDesignsByTheme
} from './DesignsMetadata/bundles.metadata';
import {
  promotionDesignsById,
  promotionDesignsByTheme
} from './DesignsMetadata/promotions.metadata';
import {
  rollingOffersDesignsById,
  rollingOffersDesignsByTheme
} from './DesignsMetadata/rollingOffers.metadata';
import { EThemes } from './enums';

const NEW_DESIGN_THRESHOLD = 30 * 24 * 60 * 60 * 1000; // 30 days

export const isDesignNew = (designDate: string): boolean => {
  const createdAt = new Date(designDate).getTime();
  const daysSinceCreation = Date.now() - createdAt;
  return daysSinceCreation <= NEW_DESIGN_THRESHOLD;
};

export const getDefaultBodyBlocksOrder = (
  featureFlags: Record<string, boolean>
): StoreBodyBlock[] => {
  if (!featureFlags) return DEFAULT_BODY_BLOCKS_ORDER;

  const isBlockEnabled = (bodyBlocks: StoreBodyBlock[]): StoreBodyBlock[] => {
    const featureFlagMap: Partial<Record<StoreBodyBlock, boolean>> = {
      [StoreBodyBlock.progressBars]:
        featureFlags![EFeatureFlag.DASHBOARD_PROGRESS_BAR],
      [StoreBodyBlock.rollingOffers]:
        featureFlags![EFeatureFlag.DASHBOARD_ROLLING_OFFER_FEATURE],
      [StoreBodyBlock.dailyBonusBadge]:
        featureFlags![EFeatureFlag.DASHBOARD_DAILY_BONUS_BADGE],
      [StoreBodyBlock.playerLevel]:
        featureFlags![EFeatureFlag.DASHBOARD_BODYBLOCK_SUPPORT_PLAYER_LEVEL]
    };

    return bodyBlocks.filter(
      (block: StoreBodyBlock) => featureFlagMap[block] !== false
    );
  };

  return isBlockEnabled(DEFAULT_BODY_BLOCKS_ORDER);
};

export const checkNewDesignByBlock: Partial<Record<TemplateStudioBlock, any>> =
  {
    [StoreBodyBlock.bundles]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedBundleDesignTheme: EThemes | undefined =
        bundleDesignsById[selectedDesignId]?.theme;

      const hasNewBundleDesigns = bundleDesignsByTheme[
        selectedBundleDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewBundleDesigns;
    },
    [StoreBodyBlock.specialOffers]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedPromotionDesignTheme: EThemes | undefined =
        promotionDesignsById[selectedDesignId]?.theme;

      const hasNewPromotionDesigns = promotionDesignsByTheme[
        selectedPromotionDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewPromotionDesigns;
    },
    [StoreBodyBlock.rollingOffers]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedRollingOffersDesignTheme: EThemes | undefined =
        rollingOffersDesignsById[selectedDesignId]?.theme;

      const hasNewRollingOffersDesigns = rollingOffersDesignsByTheme[
        selectedRollingOffersDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewRollingOffersDesigns;
    }
  };

export const getOutdatedBlocks = ({
  bundle,
  promotion,
  rollingOffers,
  banner
}: TemplateStudioModel & { specificBlockToCheck?: TemplateStudioBlock }) => {
  const outdatedBlocks = new Set<TemplateStudioBlock>();

  if (
    checkNewDesignByBlock[StoreBodyBlock.bundles]!(bundle?.selectedDesignId)
  ) {
    outdatedBlocks.add(StoreBodyBlock.bundles);
  }

  if (
    checkNewDesignByBlock[StoreBodyBlock.specialOffers]!(
      promotion?.selectedDesignId
    )
  ) {
    outdatedBlocks.add(StoreBodyBlock.specialOffers);
  }

  if (
    checkNewDesignByBlock[StoreBodyBlock.rollingOffers]!(
      rollingOffers?.selectedDesignId
    )
  ) {
    outdatedBlocks.add(StoreBodyBlock.rollingOffers);
  }

  return outdatedBlocks;
};
